import {
  getCachedViewSnapshotForSelections,
  subscribeViewProjectionChanges,
} from '@databases/database-state';
import { getAppMode } from '@databases/modes';
import {
  ReduxDatabaseAdapter,
  type PanelStateProvider,
} from '@databases/redux-database-adapter';
import { appStore } from '@databases/redux/store';
import {
  initializeSelectionsMachine,
  type SelectionUiInteraction,
} from '@databases/selections';
import {
  createDatabaseSelectionReducerPolicies,
  SelectionsApi,
  SelectionsApiAdapter,
  SelectionsApiReader,
} from '@databases/selections-api';
import { container, getService } from '@databases/service-container';
import { getTimelineSelectionEntryIds } from '@databases/timeline-layout/timeline-selection-entry-projection';
import { DatabaseLayoutType } from '@databases/types/database/view';
import { isBrokenDatabase } from '@databases/utils/database/utils';
import { applyDatabaseView } from '@databases/utils/database/view/view';
import {
  selectDatabaseByContentIdOptional,
  selectDatabaseByContentIdRequired,
} from '@databases/utils/redux/databases/selectors';

import { createKeyboardNavigationUsageTracker } from '../features/selections/create-keyboard-navigation-usage-tracker.ts';
import { createSelectionUiInteractionSink } from '../features/selections/create-selection-ui-interaction-sink.ts';
import {
  type SelectionFeatureActivationContext,
  SelectionFeatureKeys,
  type SelectionProjectionLifecycle,
} from '../features/types.ts';
import { filterSelectionFeatures } from '../features/utils/filter-selection-features.ts';
import { resolveFeatures } from '../features/utils/resolve-features.ts';
import { resolveSelectionFeatureLayoutType } from '../features/utils/resolve-selection-feature-layout-type.ts';

import { createSelectionsDatabaseSnapshotGetter } from './create-selections-database-snapshot-getter.ts';
import { getModeConfig } from './get-mode-config.ts';
import { type Recyclable, type RecyclableDisposeContext } from './types.ts';

/**
 * Creates the availability and invalidation boundary for projection reads.
 *
 * The database can legitimately be absent or loading while recyclable setup
 * and store notifications are running, and a broken database must be treated as
 * unavailable. Using the required selector here would turn those lifecycle
 * states into exceptions and could fail selection bootstrap. The optional
 * selector lets the lifecycle report the projection as unavailable and retain
 * its last valid baseline. Actual projection reads still go through the
 * `DatabaseAdapter`, whose required selector is safe after this availability
 * gate succeeds.
 */
function createSelectionProjectionLifecycle(
  contentId: string,
): SelectionProjectionLifecycle {
  const getDatabase = () =>
    selectDatabaseByContentIdOptional(appStore.getState(), { contentId });

  return {
    isAvailable: () => {
      const database = getDatabase();
      return database !== undefined && !isBrokenDatabase(database);
    },
    subscribe: (onChange) => {
      let database = getDatabase();
      const unsubscribeDatabase = appStore.subscribe(() => {
        const nextDatabase = getDatabase();

        if (nextDatabase !== database) {
          database = nextDatabase;
          onChange();
        }
      });
      const unsubscribeView = subscribeViewProjectionChanges(onChange);

      return () => {
        unsubscribeView();
        unsubscribeDatabase();
      };
    },
  };
}
function initializeSelectionsMachineRecyclable(): Recyclable {
  let destroy: VoidFunction | undefined;
  let unsubscribePermissions: VoidFunction | undefined;
  let removeSelectionUiDocumentListeners: VoidFunction | undefined;
  let keyboardNavigationUsageTracker:
    | ReturnType<typeof createKeyboardNavigationUsageTracker>
    | undefined;

  function initialize(currentContentId: string) {
    const permissionsManager = getService('PermissionsManager');
    const panelManager = getService('PanelManager');
    const layoutType = getCachedViewSnapshotForSelections().settings.layout.type;
    const panelStateProvider: PanelStateProvider = {
      getPanelEntryId: () => {
        if (panelManager.state.panel !== 'EntryViewPanel') {
          return null;
        }
        return panelManager.getData('EntryViewPanel')?.entryId ?? null;
      },
    };

    const getDatabaseSnapshot = createSelectionsDatabaseSnapshotGetter({
      getState: appStore.getState,
      getView: getCachedViewSnapshotForSelections,
      selectDatabase: selectDatabaseByContentIdRequired,
      applyView: applyDatabaseView,
      projectEntryIds:
        layoutType === DatabaseLayoutType.TIMELINE
          ? getTimelineSelectionEntryIds
          : undefined,
    });

    const databaseAdapter = new ReduxDatabaseAdapter({
      getContentId: () => currentContentId,
      getDatabaseSnapshot,
      getState: appStore.getState,
      panelStateProvider,
    });
    const projectionLifecycle = createSelectionProjectionLifecycle(currentContentId);

    removeSelectionUiDocumentListeners?.();

    const selectionUiSink = createSelectionUiInteractionSink({
      contentId: currentContentId,
      databaseAdapter,
      canEditDatabaseContent: () => permissionsManager.getPermissions().canEditContent,
    });

    const onSelectionUiInteraction = (event: SelectionUiInteraction) => {
      if (layoutType === DatabaseLayoutType.TABLE) {
        selectionUiSink.emit(event);
      }

      if (event.source === 'keyboard') {
        keyboardNavigationUsageTracker?.record(event.detail.actionFamily);
      }
    };

    const { setup, machine } = initializeSelectionsMachine({
      reducerPolicies: createDatabaseSelectionReducerPolicies({
        canEditContent: () => permissionsManager.getPermissions().canEditContent,
        adapter: databaseAdapter,
      }),
    });

    const selectionsApi = new SelectionsApi({
      machine,
      adapter: databaseAdapter,
    });

    // Now that we have concrete instances, register them immediately.
    container.registerSingleton('SelectionsApi', () => selectionsApi);
    container.registerSingleton('SelectionsActions', () => {
      return new SelectionsApiAdapter({ selectionsApi });
    });
    container.registerSingleton('SelectionsReader', () => {
      return new SelectionsApiReader({
        selectionsApi,
        adapter: databaseAdapter,
      });
    });

    /**
     * Selection UI stabilized metrics use rAF; when the tab is hidden or the page unloads,
     * cancel only this sink's six in-flight refs **silently** (no Reliability rows) so
     * durations are not inflated across background time (see `createSelectionUiInteractionSink`).
     */
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        selectionUiSink.cancelInFlightMetrics();
      }
    };

    const onPageHide = () => {
      selectionUiSink.cancelInFlightMetrics();
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pagehide', onPageHide);

    removeSelectionUiDocumentListeners = () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pagehide', onPageHide);
    };

    const modeConfig = getModeConfig(getAppMode());

    const stopFeatures = setup(({ inputCapture, sharedLoop }) => {
      /**
       * The mode determines which features are enabled.
       * There are also some items that despite the mode, are always enabled
       * as they are required for syncing changes, etc.
       */
      const { modeFeatures, alwaysOnFeatures } = modeConfig.buildSelectionFeatures({
        inputCapture,
        sharedLoop,
        databaseAdapter,
        projectionLifecycle,
        contentId: currentContentId,
        onSelectionUiInteraction,
      });

      const activationContext: SelectionFeatureActivationContext = {
        permissions: permissionsManager.getPermissions(),
        layoutType: resolveSelectionFeatureLayoutType(layoutType),
      };
      const features = [
        ...modeFeatures.inputs,
        ...modeFeatures.effects,
        ...alwaysOnFeatures,
      ];
      const activeFeatureSet = new Set(
        [
          activationContext,
          ...(activationContext.layoutType === DatabaseLayoutType.TIMELINE
            ? [{ ...activationContext, layoutType: DatabaseLayoutType.TABLE }]
            : []),
        ].flatMap((context) => filterSelectionFeatures(features, context)),
      );
      const activeFeatures = features.filter((feature) => activeFeatureSet.has(feature));

      if (
        activeFeatures.some(
          (feature) =>
            feature.key === SelectionFeatureKeys.TABLE_NAVIGATION_KEYBOARD_INPUT ||
            feature.key === SelectionFeatureKeys.TABLE_TAB_NAVIGATION_KEYBOARD_INPUT,
        )
      ) {
        keyboardNavigationUsageTracker?.markAvailable();
      }

      return resolveFeatures(activeFeatures);
    });

    destroy = () => {
      removeSelectionUiDocumentListeners?.();
      removeSelectionUiDocumentListeners = undefined;
      selectionUiSink.dispose();
      stopFeatures();
    };
  }

  return {
    recycleOn: ['view-id-changed', 'layout-type-changed'],

    setup(contentId) {
      keyboardNavigationUsageTracker ??= createKeyboardNavigationUsageTracker({
        contentId,
      });
      initialize(contentId);

      // Permission changes affect only selection feature policy, so this inner lifecycle stays local.
      unsubscribePermissions?.();

      const permissionsManager = getService('PermissionsManager');

      unsubscribePermissions = permissionsManager.subscribe(() => {
        destroy?.();
        initialize(contentId);
      });
    },

    dispose(context?: RecyclableDisposeContext) {
      unsubscribePermissions?.();
      unsubscribePermissions = undefined;
      destroy?.();
      destroy = undefined;

      if (context?.reason !== 'view-identity-changed') {
        keyboardNavigationUsageTracker?.dispose();
        keyboardNavigationUsageTracker = undefined;
      }
    },
  };
}

/**
 * Owns the recyclable selection lifecycle. View and layout changes rebuild
 * feature composition without resetting database-scoped keyboard usage tracking.
 */
export function initializeRecyclableSelectionsMachine(): Recyclable {
  return initializeSelectionsMachineRecyclable();
}
